import { z } from "zod";
import type { PermissionId } from "@/modules/roles/domain/contracts/permissions";
import {
  parseRepeaterSettings,
  validateRepeaterSettings,
} from "@/modules/forms/domain/lib/form-repeater-settings";
import {
  formFieldTypes,
  formStatuses,
} from "@/server/platform/db/schema/forms";
import { OffsetPageQuerySchema } from "@/shared/contracts/base";

export const formsReadPermission = "forms:read" satisfies PermissionId;
export const formsCreatePermission = "forms:create" satisfies PermissionId;
export const formsUpdatePermission = "forms:update" satisfies PermissionId;
export const formsDeletePermission = "forms:delete" satisfies PermissionId;

export const FormStatusSchema = z.enum(formStatuses);
export const FormFieldTypeSchema = z.enum(formFieldTypes);

export const FormSettingsSchema = z
  .object({
    deadline: z.string().optional(),
    maxResponses: z.number().int().positive().optional(),
    posterKey: z.string().min(1).optional(),
    viewerAccessExpiresAt: z.string().min(1).optional(),
  })
  .strict();

export type FormSettingsInput = z.infer<typeof FormSettingsSchema>;

export const FormsListQuerySchema = OffsetPageQuerySchema.extend({
  status: FormStatusSchema.optional(),
  categoryId: z.string().min(1).optional(),
});

const FormFieldOptionItemSchema = z.object({
  value: z.string(),
  label: z.string(),
  enabled: z.boolean().optional(),
});

export type EffectiveFieldPayload = {
  type: z.infer<typeof FormFieldTypeSchema>;
  label: string;
  description?: string | null;
  placeholder?: string | null;
  required: boolean;
  options?: z.infer<typeof FormFieldOptionItemSchema>[] | null;
  settings?: Record<string, unknown> | null;
};

/** คืน error code สั้น ๆ สำหรับ map เป็น HTTP 400 */
export function validateEffectiveField(
  data: EffectiveFieldPayload,
): string | null {
  if (data.type === "section" && data.required) {
    return "SECTION_REQUIRED_INVALID";
  }
  const needsOptions =
    data.type === "choice" ||
    data.type === "dropdown" ||
    data.type === "checkbox";
  const opts = data.options;
  if (needsOptions) {
    if (
      !Array.isArray(opts) ||
      opts.length === 0 ||
      !opts.every((o) => o.label.length > 0)
    ) {
      return "OPTIONS_REQUIRED";
    }
    const hasSelectable = opts.some(
      (o) => o.enabled !== false && o.label.length > 0,
    );
    if (!hasSelectable) return "OPTIONS_NONE_ENABLED";
  }
  if (data.type === "rating") {
    const mrRaw = data.settings?.maxRating;
    const mr = typeof mrRaw === "number" ? mrRaw : undefined;
    if (mr !== undefined && mr !== 5 && mr !== 10) {
      return "RATING_MAX_INVALID";
    }
  }
  if (data.type === "number") {
    const st = data.settings;
    const minRaw = st?.min;
    const maxRaw = st?.max;
    const intRaw = st?.integerOnly;
    if (
      minRaw !== undefined &&
      (typeof minRaw !== "number" || !Number.isFinite(minRaw))
    ) {
      return "NUMBER_MIN_INVALID";
    }
    if (
      maxRaw !== undefined &&
      (typeof maxRaw !== "number" || !Number.isFinite(maxRaw))
    ) {
      return "NUMBER_MAX_INVALID";
    }
    if (intRaw !== undefined && typeof intRaw !== "boolean") {
      return "NUMBER_INTEGER_FLAG_INVALID";
    }
    if (
      typeof minRaw === "number" &&
      Number.isFinite(minRaw) &&
      typeof maxRaw === "number" &&
      Number.isFinite(maxRaw) &&
      minRaw > maxRaw
    ) {
      return "NUMBER_RANGE_INVERTED";
    }
  }
  if (data.type === "repeater") {
    const parsed = parseRepeaterSettings(data.settings);
    if (!parsed) return "REPEATER_SETTINGS_INVALID";
    const err = validateRepeaterSettings(parsed);
    if (err) return err;
  }
  return null;
}

const refineCreateField = (
  data: EffectiveFieldPayload,
  ctx: z.RefinementCtx,
) => {
  const err = validateEffectiveField(data);
  if (err) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: err });
  }
};

const FieldBodyBaseSchema = z.object({
  type: FormFieldTypeSchema,
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  placeholder: z.string().nullable().optional(),
  required: z.boolean().optional(),
  options: z.array(FormFieldOptionItemSchema).nullable().optional(),
  settings: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const CreateFieldBodySchema = FieldBodyBaseSchema.superRefine((d, ctx) =>
  refineCreateField(
    {
      ...d,
      required: d.required ?? false,
    },
    ctx,
  ),
).transform((d) => ({
  ...d,
  required: d.required ?? false,
}));

export const UpdateFieldBodySchema = z
  .object({
    type: FormFieldTypeSchema.optional(),
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    placeholder: z.string().nullable().optional(),
    required: z.boolean().optional(),
    options: z.array(FormFieldOptionItemSchema).nullable().optional(),
    settings: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strict();

export const CreateFormBodySchema = z.object({
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  categoryId: z.string().min(1).nullable().optional(),
  settings: FormSettingsSchema.nullable().optional(),
  fields: z.array(CreateFieldBodySchema).optional().default([]),
});

const ViewerPinBodySchema = z.string().regex(/^\d{6}$/, "VIEWER_PIN_INVALID");

export const PatchFormBodySchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  categoryId: z.string().min(1).nullable().optional(),
  settings: FormSettingsSchema.nullable().optional(),
  /** ຕັ້ງ/ປ່ຽນ PIN ເບິ່ງຄຳຕອບ (6 ຫຼັກ) — ບໍ່ສົ່ງກັບໃນ settings */
  viewerPin: ViewerPinBodySchema.optional(),
});

export const PatchFormStatusBodySchema = z.object({
  status: FormStatusSchema,
});

export const ReorderFieldsBodySchema = z.object({
  orderedFieldIds: z.array(z.string().min(1)).min(1),
});

export type FormsListQueryDTO = z.infer<typeof FormsListQuerySchema>;

export function canTransitionFormStatus(
  from: z.infer<typeof FormStatusSchema>,
  to: z.infer<typeof FormStatusSchema>,
): boolean {
  if (from === to) return true;
  if (from === "closed") return false;
  if (from === "draft") return to === "published" || to === "closed";
  if (from === "published") return to === "closed" || to === "draft";
  return false;
}
